import { Controller } from "@hotwired/stimulus";

export default class extends Controller {
  static values = {
    defaultLessonDuration: { type: Number, default: 60 },
    zeroPickupLabel: { type: String, default: "0 minutes (test only)" },
    lessonTypePickupSync: { type: Boolean, default: false },
  };

  connect() {
    this.handleFrameLoad = this.handleMasterLessonFrameLoad.bind(this);
    document.addEventListener('turbo:frame-load', this.handleFrameLoad);

    this.observeInstructorLocationForm();

    if (this.lessonTypePickupSyncValue) {
      requestAnimationFrame(() => this.syncPickupDurationFromLessonType());
    }

    // Auto-close quicklook if data attribute is true
    const autoCloseValue =
      this.element && this.element.dataset
        ? this.element.dataset.schoolLessonQuickLookLessonDetailsAutoCloseValue
        : null;

    const masterLessonIdValue =
      this.element && this.element.dataset
        ? this.element.dataset
            .schoolLessonQuickLookLessonDetailsMasterLessonIdValue
        : null;

    if ((autoCloseValue === "true" || autoCloseValue === true)) {
      if(masterLessonIdValue && masterLessonIdValue !== "0") {
        this.showMasterQuickLook(masterLessonIdValue);
      } else {
        alert("Lesson successfully updated as an observation. Closing quick look.");
        this.closeQuickLook({
          preventDefault: () => {},
        });
      }
    }
  }

  disconnect() {
    document.removeEventListener('turbo:frame-load', this.handleFrameLoad);
    if (this.observer) {
      this.observer.disconnect();
    }
  }

  handleMasterLessonFrameLoad(event) {
    if (event.target.id !== 'master_lesson_view_and_edit') return;
    const typeDropdownWrapper = document.getElementById('type-dropdown-wrapper');
    if (!typeDropdownWrapper) return;
    const hasForm = event.target.querySelector('form');
    if (hasForm) {
      typeDropdownWrapper.classList.remove('u-hidden');
    } else {
      typeDropdownWrapper.classList.add('u-hidden');
    }
  }

  async showMasterQuickLook(masterLessonId) {
    const url = `/schools/master-lesson/quick-look/${masterLessonId}`;
    const csrfToken = document.querySelector('meta[name="csrf-token"]').content;

    const response = await fetch(url, {
      method: "GET",
      headers: {
        "Content-Type": "application/x-www-form-urlencoded",
        "X-CSRF-Token": csrfToken,
        Accept: "text/vnd.turbo-stream.html",
      },
    });

    if (response.ok) {
      const quickViewElement = document.querySelector(
        "#school-dashboard-lesson-quick-look"
      );
      quickViewElement.classList.remove("u-hidden");

      const responseBody = await response.text();
      const parser = new DOMParser();
      const xmlDoc = parser.parseFromString(responseBody, "text/html");
      const template = xmlDoc.querySelector("template");
      const targetElement = document.getElementById("school_lesson_quick_look");

      if (template && targetElement) {
        targetElement.innerHTML = "";
        targetElement.appendChild(template.content.cloneNode(true));
      }
    }
  }

  observeInstructorLocationForm() {
    const instructorLocationContainer = document.getElementById(
      "school_quick_look_add_lesson_instructor_and_location_form"
    );

    if (instructorLocationContainer) {
      this.observer = new MutationObserver(() => {
        this.checkForLocationEdit();
      });

      this.observer.observe(instructorLocationContainer, {
        childList: true,
        subtree: true,
      });
    }
  }

  checkForLocationEdit() {
    const locationEditForm = document.getElementById(
      "quicklook-schedule-location-edit"
    );
    const saveButtonContainer = document.getElementById(
      "school-lesson-quick-look-lesson-save-button"
    );

    if (saveButtonContainer) {
      const saveButton = saveButtonContainer.querySelector("button");

      if (saveButton) {
        if (locationEditForm) {
          saveButton.setAttribute("disabled", "disabled");
        } else {
          saveButton.removeAttribute("disabled");
        }
      }
    }
  }

  async editLesson(event) {
    event.preventDefault();

    const lesson_id = 45;
    const csrfToken = document.querySelector('meta[name="csrf-token"]').content;
  }

  getLessonTypeFromForm() {
    const el = document.getElementById("lesson_type");
    return el ? String(el.value || "").trim() : "";
  }

  ensureZeroPickupOption() {
    const sel = document.getElementById("new_lesson_specific_duration_in_minutes");
    if (!sel) return;
    if (sel.querySelector('option[value="0"]')) return;
    const opt = document.createElement("option");
    opt.value = "0";
    opt.textContent = this.zeroPickupLabelValue;
    sel.insertBefore(opt, sel.firstChild);
  }

  removeZeroPickupOption() {
    const sel = document.getElementById("new_lesson_specific_duration_in_minutes");
    if (!sel) return;
    const opt = sel.querySelector('option[value="0"]');
    if (opt) opt.remove();
    if (sel.value === "0") {
      const fallback = String(this.defaultLessonDurationValue);
      if ([...sel.options].some((o) => o.value === fallback)) {
        sel.value = fallback;
      } else if (sel.options.length) {
        sel.selectedIndex = 0;
      }
    }
  }

  /** Scheduled drive test: pre-test note + optional 0 min pickup. Open slots never use 0 here (slot length must be > 0). */
  syncPickupDurationFromLessonType() {
    if (!this.lessonTypePickupSyncValue) return;

    const t = this.getLessonTypeFromForm();
    const noteRow = document.getElementById("pickup-pre-test-duration-note-row");

    if (t === "DriveTest") {
      this.ensureZeroPickupOption();
      noteRow?.classList.remove("u-hidden");
      return;
    }

    // Open-slot flows reuse this duration for slot length; server requires duration > 0.
    this.removeZeroPickupOption();
    noteRow?.classList.add("u-hidden");
  }

  handleTypeSelection(event) {
    const type = event.detail?.dispatchItem?.id;
    const fakeEvent = { preventDefault: () => {} };
    switch (type) {
      case "Lesson":            this.showOriginalForm(); this.hideTestDetails(fakeEvent);        break;
      case "DriveTest":         this.showOriginalForm(); this.unhideTestDetails(fakeEvent);      break;
      case "OpenSlotLesson":    this.showOriginalForm(); this.setupSlotDetails(fakeEvent); break;
      case "OpenSlotDriveTest": this.showOriginalForm(); this.setupSlotDetails(fakeEvent); break;
      case "Classroom":         this.showOriginalForm(); this.setupScheduleClassroom(fakeEvent); break;
      case "Observation":       this.showObservationForm(); break;
    }
  }

  showOriginalForm() {
    const lessonFormContent = document.getElementById("lesson-form-content");
    const masterForm = document.getElementById("masterform");
    const typeDropdownWrapper = document.getElementById("type-dropdown-wrapper");
    if (lessonFormContent) lessonFormContent.classList.remove("u-hidden");
    if (masterForm) masterForm.classList.add("u-hidden");
    if (typeDropdownWrapper) typeDropdownWrapper.classList.remove("u-hidden");
  }

  showObservationForm() {
    const lessonFormContent = document.getElementById("lesson-form-content");
    const masterForm = document.getElementById("masterform");
    if (lessonFormContent) lessonFormContent.classList.add("u-hidden");
    if (masterForm) masterForm.classList.remove("u-hidden");

    const pickupPreTestNoteRow = document.getElementById(
      "pickup-pre-test-duration-note-row"
    );
    if (pickupPreTestNoteRow) pickupPreTestNoteRow.classList.add("u-hidden");

    this.removeZeroPickupOption();
  }

  closeQuickLook(event) {
    event.preventDefault();

    const quickViewElement = document.querySelector(
      "#school-dashboard-lesson-quick-look"
    );
    quickViewElement.classList.add("u-hidden");
  }

  hideTestDetails(event) {
    event.preventDefault();

    const formElement = document.getElementById("test_details_form");
    if (formElement) formElement.setAttribute("hidden", true);

    const formElementStudent = document.getElementById(
      "quick-look-student-book-dropdown"
    );
    formElementStudent.removeAttribute("hidden");

    const formElementSlot = document.getElementById("add-open-slots-form");
    formElementSlot.setAttribute("hidden", true);

    const formElementPickupTime = document.getElementById(
      "add-lesson-pickup-time-form"
    );
    formElementPickupTime.removeAttribute("hidden");

    const formElementSaveButton = document.getElementById(
      "school-lesson-quick-look-lesson-save-button"
    );
    formElementSaveButton.removeAttribute("hidden");

    const formElementScheduleClassroom = document.querySelector(
      "#schedule-classroom-program-form"
    );
    formElementScheduleClassroom.classList.add("u-hidden");

    const pickupPreTestNoteRow = document.getElementById(
      "pickup-pre-test-duration-note-row"
    );
    if (pickupPreTestNoteRow) pickupPreTestNoteRow.classList.add("u-hidden");

    const pickupDurationSelect = document.getElementById(
      "new_lesson_specific_duration_in_minutes"
    );
    if (
      pickupDurationSelect &&
      pickupDurationSelect.value === "0"
    ) {
      pickupDurationSelect.value = String(this.defaultLessonDurationValue);
    }

    this.removeZeroPickupOption();
  }

  unhideTestDetails(event) {
    event.preventDefault();

    this.ensureZeroPickupOption();

    const formElement = document.getElementById("test_details_form");
    if (formElement) formElement.removeAttribute("hidden");

    const formElementStudent = document.getElementById(
      "quick-look-student-book-dropdown"
    );
    formElementStudent.removeAttribute("hidden");

    const formElementPickupTime = document.getElementById(
      "add-lesson-pickup-time-form"
    );
    formElementPickupTime.removeAttribute("hidden");

    const formElementSaveButton = document.getElementById(
      "school-lesson-quick-look-lesson-save-button"
    );
    formElementSaveButton.removeAttribute("hidden");

    const formElementSlot = document.getElementById("add-open-slots-form");
    formElementSlot.setAttribute("hidden", false);

    const formElementScheduleClassroom = document.querySelector(
      "#schedule-classroom-program-form"
    );
    formElementScheduleClassroom.classList.add("u-hidden");

    const pickupPreTestNoteRow = document.getElementById(
      "pickup-pre-test-duration-note-row"
    );
    if (pickupPreTestNoteRow) pickupPreTestNoteRow.classList.remove("u-hidden");

    const pickupDurationSelect = document.getElementById(
      "new_lesson_specific_duration_in_minutes"
    );
    if (
      pickupDurationSelect &&
      String(pickupDurationSelect.value) ===
        String(this.defaultLessonDurationValue)
    ) {
      pickupDurationSelect.value = "0";
    }
  }

  setupSlotDetails(event) {
    event.preventDefault();

    const formElement = document.getElementById(
      "quick-look-student-book-dropdown"
    );
    formElement.setAttribute("hidden", true);

    const formElementTest = document.getElementById("test_details_form");
    if (formElementTest) formElementTest.setAttribute("hidden", true);

    const formElementSlot = document.getElementById("add-open-slots-form");
    formElementSlot.removeAttribute("hidden");

    const formElementPickupTime = document.getElementById(
      "add-lesson-pickup-time-form"
    );
    formElementPickupTime.removeAttribute("hidden");

    const formElementSaveButton = document.getElementById(
      "school-lesson-quick-look-lesson-save-button"
    );
    formElementSaveButton.setAttribute("hidden", true);

    const formElementInstructor = document.getElementById(
      "school_quick_look_add_lesson_instructor_and_location"
    );
    formElementInstructor.setAttribute("hidden", true);

    const formElementScheduleClassroom = document.querySelector(
      "#schedule-classroom-program-form"
    );
    formElementScheduleClassroom.classList.add("u-hidden");

    const pickupPreTestNoteRow = document.getElementById(
      "pickup-pre-test-duration-note-row"
    );
    if (pickupPreTestNoteRow) pickupPreTestNoteRow.classList.add("u-hidden");

    this.removeZeroPickupOption();
  }

  togglePackageList(event) {
    event.preventDefault();

    const toggle = event.currentTarget;
    const packageList = event.currentTarget.closest(".QuicklookPackageList");
    if (!packageList) return;

    const panel = packageList.querySelector(".QuicklookPackageList__panel");
    const label = packageList.querySelector("[data-quicklook-package-list-label]");
    if (!panel || !toggle || !label) return;

    const isOpen = toggle.getAttribute("aria-expanded") === "true";

    if (isOpen) {
      this.closePackageList({ packageList, panel, toggle, label });
    } else {
      this.openPackageList({ packageList, panel, toggle, label });
    }
  }

  openPackageList({ panel, toggle, label }) {
    toggle.setAttribute("aria-expanded", "true");
    panel.setAttribute("aria-hidden", "false");
    label.textContent = "hide packages";
    panel.style.maxHeight = `${panel.scrollHeight}px`;
    panel.classList.add("is-open");
  }

  closePackageList({ packageList, panel, toggle, label }) {
    toggle.setAttribute("aria-expanded", "false");
    panel.setAttribute("aria-hidden", "true");
    label.textContent = packageList.dataset.quicklookPackageListClosedLabel || "view more";
    panel.style.maxHeight = "0px";
    panel.classList.remove("is-open");
  }

  setupScheduleClassroom(event) {
    event.preventDefault();

    // const formElement = document.getElementById('quick-look-student-book-dropdown');
    // formElement.setAttribute('hidden', true);

    const formElementTest = document.getElementById("test_details_form");
    if (formElementTest) formElementTest.setAttribute("hidden", true);

    const formElementSlot = document.getElementById("add-open-slots-form");
    formElementSlot.setAttribute("hidden", true);

    const formElementInstructor = document.getElementById(
      "school_quick_look_add_lesson_instructor_and_location"
    );
    formElementInstructor.setAttribute("hidden", true);

    const formElementPickupTime = document.getElementById(
      "add-lesson-pickup-time-form"
    );
    formElementPickupTime.setAttribute("hidden", true);

    const formElementSaveButton = document.getElementById(
      "school-lesson-quick-look-lesson-save-button"
    );
    formElementSaveButton.setAttribute("hidden", true);

    const formElementStudent = document.getElementById(
      "quick-look-student-book-dropdown"
    );
    formElementStudent.removeAttribute("hidden");

    // show schedule-classroom-program-form
    const formElementScheduleClassroom = document.querySelector(
      "#schedule-classroom-program-form"
    );
    formElementScheduleClassroom.classList.remove("u-hidden");

    const pickupPreTestNoteRow = document.getElementById(
      "pickup-pre-test-duration-note-row"
    );
    if (pickupPreTestNoteRow) pickupPreTestNoteRow.classList.add("u-hidden");
    this.removeZeroPickupOption();
  }
}
